/**
 * Admin Reports Screen.
 * Lists reports with search, filter, and detail modal.
 */

import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/app/PageHeader';
import { LoadingOverlay } from '@/app/Spinner';
import { EmptyState } from '@/app/EmptyState';
import { Pagination } from '@/app/Pagination';
import { Modal } from '@/app/Modal';
import { classifyError } from '@/api/errors';
import * as reportsApi from '@/api/reportsApi';
import type { Report } from '@/api/types';

export function ReportsScreen() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const limit = 20;

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await reportsApi.listReports({
        search: search || undefined,
        status: statusFilter || undefined,
        limit,
        cursor: page > 1 ? String((page - 1) * limit) : undefined,
      });
      setReports(result.data);
      setTotal(result.pagination.total);
      setHasMore(result.pagination.has_more);
    } catch (err) {
      setError(classifyError(err).message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchReports();
  };

  const handleStatusChange = (status: string) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleArchiveReport = async (reportId: string, title: string) => {
    if (
      !window.confirm(
        `Archive "${title || 'Untitled'}"?\n\nThe report will be moved to Archived status and won't appear in normal user views.`,
      )
    ) {
      return;
    }
    setActionLoading(true);
    try {
      await reportsApi.updateReport(reportId, { status: 'archived' });
      await fetchReports();
      if (selectedReport?.id === reportId) {
        setSelectedReport({ ...selectedReport, status: 'archived' });
      }
    } catch (err) {
      alert(classifyError(err).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteReport = async (reportId: string, title: string) => {
    if (
      !window.confirm(
        `Delete "${title || 'Untitled'}"?\n\nThis action is permanent and cannot be undone.`,
      )
    ) {
      return;
    }
    setActionLoading(true);
    try {
      await reportsApi.deleteReport(reportId);
      await fetchReports();
      if (selectedReport?.id === reportId) {
        setSelectedReport(null);
      }
    } catch (err) {
      alert(classifyError(err).message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="page-content">
      <PageHeader title="Reports" subtitle={`${total.toLocaleString()} total reports`} />

      {/* Filter Bar */}
      <div className="filter-bar">
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flex: 1 }}>
          <div className="search-input" style={{ position: 'relative', flex: 1 }}>
            <SearchIcon />
            <input
              type="text"
              className="input"
              placeholder="Search by title..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: '36px', width: '100%' }}
            />
          </div>
          <button type="submit" className="btn btn--primary">
            Search
          </button>
        </form>

        <select
          className="select"
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="archived">Archived</option>
          <option value="deleted">Deleted</option>
        </select>
      </div>

      {/* Error Banner */}
      {error && <div className="alert alert--error mb-16">{error}</div>}

      {/* Loading / Empty */}
      {loading ? (
        <LoadingOverlay />
      ) : reports.length === 0 ? (
        <EmptyState message="No reports found matching your criteria." />
      ) : (
        <>
          <div className="card">
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Author</th>
                    <th>Status</th>
                    <th>Photos</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id}>
                      <td>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => setSelectedReport(report)}
                          style={{
                            textAlign: 'left',
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {report.title || 'Untitled'}
                        </button>
                      </td>
                      <td className="muted">{report.user?.username ?? '—'}</td>
                      <td>
                        <span className={`pill pill--${report.status}`}>{report.status}</span>
                      </td>
                      <td>{report.photo_count ?? 0}</td>
                      <td className="muted text-sm">{formatDate(report.created_at)}</td>
                      <td className="muted text-sm">{formatDate(report.updated_at)}</td>
                      <td>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => setSelectedReport(report)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            page={page}
            limit={limit}
            total={total}
            hasMore={hasMore}
            onPageChange={setPage}
          />
        </>
      )}

      {/* Report Detail Modal */}
      <Modal
        open={!!selectedReport}
        onClose={() => setSelectedReport(null)}
        title="Report Details"
        footer={
          <>
            {selectedReport?.status === 'draft' && (
              <button
                className="btn btn--primary"
                onClick={() =>
                  selectedReport && handleArchiveReport(selectedReport.id, selectedReport.title)
                }
                disabled={actionLoading}
              >
                Archive
              </button>
            )}
            {selectedReport?.status === 'submitted' && (
              <button
                className="btn btn--primary"
                onClick={() =>
                  selectedReport && handleArchiveReport(selectedReport.id, selectedReport.title)
                }
                disabled={actionLoading}
              >
                Archive
              </button>
            )}
            <button
              className="btn btn--danger-outline"
              onClick={() =>
                selectedReport && handleDeleteReport(selectedReport.id, selectedReport.title)
              }
              disabled={actionLoading}
            >
              Delete Report
            </button>
            <button className="btn btn--ghost" onClick={() => setSelectedReport(null)}>
              Close
            </button>
          </>
        }
      >
        {selectedReport && (
          <div className="stack">
            <div className="field">
              <label>Report ID</label>
              <input className="input" value={selectedReport.id} readOnly />
            </div>

            <div className="field">
              <label>Title</label>
              <input className="input" value={selectedReport.title || 'Untitled'} readOnly />
            </div>

            <div className="field">
              <label>Author</label>
              <input
                className="input"
                value={selectedReport.user?.username ?? selectedReport.user_id}
                readOnly
              />
            </div>

            <div className="row gap-8">
              <div className="field flex-1">
                <label>Status</label>
                <span className={`pill pill--${selectedReport.status}`}>
                  {selectedReport.status}
                </span>
              </div>
              <div className="field flex-1">
                <label>Version</label>
                <input className="input" value={`v${selectedReport.version}`} readOnly />
              </div>
            </div>

            <div className="field">
              <label>Content</label>
              <textarea
                className="textarea"
                value={selectedReport.content || 'No content'}
                readOnly
                style={{ minHeight: 200 }}
              />
            </div>

            <div className="grid-2">
              <div className="field">
                <label>Created At</label>
                <input className="input" value={formatDate(selectedReport.created_at)} readOnly />
              </div>
              <div className="field">
                <label>Updated At</label>
                <input className="input" value={formatDate(selectedReport.updated_at)} readOnly />
              </div>
            </div>

            {selectedReport.submitted_at && (
              <div className="field">
                <label>Submitted At</label>
                <input className="input" value={formatDate(selectedReport.submitted_at)} readOnly />
              </div>
            )}

            <div className="field">
              <label>Photos</label>
              <input
                className="input"
                value={`${selectedReport.photo_count ?? 0} photo(s)`}
                readOnly
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      style={{
        position: 'absolute',
        left: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 16,
        height: 16,
        color: 'var(--color-text-muted)',
      }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
